/**
 * Pure helpers for the inline "Add Section" picker on the classroom settings
 * page. The picker is an editable Select whose value is a section NAME —
 * either one picked from the list or one the user typed — so a typed "a" and a
 * picked "A" resolve to the same existing section instead of a duplicate.
 */

export interface SectionChoice {
  id: string;
  name: string;
}

export type SectionInputResolution =
  | { kind: 'empty' }
  | { kind: 'existing'; section: SectionChoice }
  | { kind: 'attached'; section: SectionChoice }
  | { kind: 'new'; name: string };

const normalise = (name: string) => name.trim().toLowerCase();

/** Exact (trimmed) match first, then case-insensitive — names are unique per school. */
export function findSectionByName(sections: SectionChoice[], name: string): SectionChoice | undefined {
  const trimmed = name.trim();
  if (!trimmed) return undefined;
  return sections.find((section) => section.name.trim() === trimmed)
    ?? sections.find((section) => normalise(section.name) === normalise(trimmed));
}

/**
 * Decide what saving the picker's current value would do for one classroom:
 * attach an existing section, create a new one, or nothing (empty / already
 * attached to this classroom).
 */
export function resolveSectionInput(
  input: string | null | undefined,
  sections: SectionChoice[],
  attachedSectionIds: ReadonlySet<string>,
): SectionInputResolution {
  const name = (input ?? '').trim();
  if (!name) return { kind: 'empty' };

  const section = findSectionByName(sections, name);
  if (!section) return { kind: 'new', name };

  return attachedSectionIds.has(String(section.id))
    ? { kind: 'attached', section }
    : { kind: 'existing', section };
}

export const canSaveSectionInput = (resolution: SectionInputResolution) =>
  resolution.kind === 'existing' || resolution.kind === 'new';

/** Inline message for a resolution that cannot be saved as-is. */
export function sectionInputMessage(resolution: SectionInputResolution, classroomName?: string | null): string {
  if (resolution.kind === 'empty') return 'Pick a section or type a new section name.';
  if (resolution.kind === 'attached') {
    return `${classroomName || 'This classroom'} already has section ${resolution.section.name}.`;
  }
  return '';
}

/** Section options a classroom can still take — the ones it already has are left out. */
export function selectableSectionOptions(sections: SectionChoice[], attachedSectionIds: ReadonlySet<string>) {
  return sections
    .filter((section) => !attachedSectionIds.has(String(section.id)))
    .map((section) => ({ label: section.name, value: section.name }));
}

/** e.g. "JSS 1" + "A" → "JSS 1 A", the name the classroom section is stored under. */
export const classroomSectionName = (classroomName: string | null | undefined, sectionName: string) =>
  (classroomName ? `${classroomName} ${sectionName}` : sectionName);
